import { z } from "zod";
import { PAGE_SIZE_MAX, keysetQuery, paginationQuery } from "../pagination";
import { MEET_CURSOR_PATTERN, type MeetSportSummary, type MeetStatusCount } from "./meets";
import type { ResultStatus, ScheduleStatus } from "./vocab";

/**
 * Mission control — the games' administrators watching every venue at once.
 * Read-only, `COMPETITION_SETUP_ROLES` (SUPER_ADMIN, ADMIN): the board names
 * operators' coverage and the activity feed names who wrote what.
 *
 *   GET /meets/alerts?games_id=                Paginated<MeetAlert>        what needs someone now
 *   GET /meets/status-counts/by-venue?games_id= Paginated<MeetVenueBoard>  the venue × sport grid
 *   GET /meets/activity?games_id=              Keyset<MeetActivityItem>    every competition write, newest first
 *   GET /meets/:id/activity                    Keyset<MeetActivityItem>    one meet's history
 *
 * Every number is counted in Postgres, in one statement per call (CLAUDE.md
 * Rule 0). Nothing here measures devices or network links: the platform does
 * not record them, so the board does not pretend to.
 */

const idField = z.string().uuid().toLowerCase();
const notYearZero = (v: string) => !v.startsWith("0000");
const dayField = z.string().date().refine(notYearZero, "Invalid date");

// ─── Alerts ──────────────────────────────────────────────────────────────────

/**
 * What an alert is about. Each is a condition on ONE top-level meet, relative
 * to now; a meet CANCELLED, POSTPONED or OFFICIAL never raises one.
 *
 *   unofficial_overdue  UNOFFICIAL for longer than `MEET_UNOFFICIAL_ALERT_MINUTES`
 *                       (since `result_status_at`): a result waits to be made official
 *   live_overrun        LIVE more than `MEET_OVERRUN_ALERT_MINUTES` past `scheduled_end`
 *   late_start          SCHEDULED / TENTATIVE with no result (NONE / START_LIST) more than
 *                       `MEET_LATE_START_ALERT_MINUTES` after `scheduled_start`. A meet
 *                       someone already marked DELAYED is not raised: it was seen.
 *   interrupted         schedule INTERRUPTED: stopped mid-meet, someone must resume it
 *
 * The order is the ribbon's: the result waiting on an official first.
 */
export const MEET_ALERT_KINDS = ["unofficial_overdue", "live_overrun", "late_start", "interrupted"] as const;
export type MeetAlertKind = (typeof MEET_ALERT_KINDS)[number];

export const MEET_UNOFFICIAL_ALERT_MINUTES = 30;
export const MEET_OVERRUN_ALERT_MINUTES = 15;
export const MEET_LATE_START_ALERT_MINUTES = 15;

/** The statuses each alert lists — so a client lists exactly the meets an alert counts with `GET /meets` (and then narrows by time). */
export const MEET_ALERT_FILTERS: Record<MeetAlertKind, { schedule_status: readonly ScheduleStatus[]; result_status: readonly ResultStatus[] }> = {
	unofficial_overdue: { schedule_status: ["TENTATIVE", "SCHEDULED", "DELAYED", "INTERRUPTED"], result_status: ["UNOFFICIAL"] },
	live_overrun: { schedule_status: ["TENTATIVE", "SCHEDULED", "DELAYED", "INTERRUPTED"], result_status: ["LIVE"] },
	late_start: { schedule_status: ["TENTATIVE", "SCHEDULED"], result_status: ["NONE", "START_LIST"] },
	interrupted: { schedule_status: ["INTERRUPTED"], result_status: ["NONE", "START_LIST", "LIVE", "UNOFFICIAL"] },
};

/** `GET /meets/alerts` — a games has tens of sports and four kinds, so one page holds them all. */
export const meetAlertsQuery = z.object({
	games_id: idField,
	page: paginationQuery.shape.page,
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_MAX),
});
export type MeetAlertsQuery = z.input<typeof meetAlertsQuery>;

/** One row of `GET /meets/alerts`: one kind in one sport. Ordered by kind (`MEET_ALERT_KINDS`), then oldest first. */
export interface MeetAlert {
	kind: MeetAlertKind;
	sport: MeetSportSummary;
	/** Top-level meets of the sport in this condition now. */
	count: number;
	/**
	 * When the oldest of them crossed into it: `result_status_at` (unofficial),
	 * `scheduled_end` (overrun), `scheduled_start` (late start),
	 * `result_status_at` else `updated_at` (interrupted). UTC.
	 */
	since: string;
	/** The meet that has been in the condition longest — where "Jump to match" goes. */
	oldest_meet_id: string;
}

// ─── The venue board ─────────────────────────────────────────────────────────

/** `GET /meets/status-counts/by-venue` — a games uses tens of venues, so one page normally holds them all. */
export const meetVenueBoardQuery = z
	.object({
		games_id: idField,
		/** Days in the games' own zone, `from` inclusive, `to` exclusive — as `GET /meets/status-counts`. */
		from: dayField.optional(),
		to: dayField.optional(),
		page: paginationQuery.shape.page,
		page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_MAX),
	})
	.superRefine((v, ctx) => {
		if (v.from !== undefined && v.to !== undefined && v.to <= v.from) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: "`to` must be after `from`" });
		}
	});
export type MeetVenueBoardQuery = z.input<typeof meetVenueBoardQuery>;

/** A top-level venue (a hall, a stadium): its courts and fields are counted inside it. */
export interface MeetBoardVenue {
	venue_id: string;
	code: string;
	name: string;
	city: string | null;
}

/**
 * Who can enter this sport's results: logins that are ACTIVE, hold the
 * OPERATION role, and sit in an active OPERATION group that holds the sport
 * and `competition.results`. `active` is those of them with a live OPERATION
 * session seen in the last `SESSION_ACTIVE_WINDOW_MINUTES` (the API's "who is
 * currently logged in") — a session, not a person at a desk.
 */
export interface MeetSportCoverage {
	assigned: number;
	active: number;
}

/** One sport played at one venue. */
export interface MeetVenueSport {
	sport: MeetSportSummary;
	/** As `GET /meets/status-counts`, for this sport at this venue. */
	counts: MeetStatusCount[];
	/** Meets of this sport at this venue in each alert condition NOW (within the day range when one is given). */
	alerts: Record<MeetAlertKind, number>;
	/** The earliest start at or after now among the counted meets that are not finished. */
	next_start_at: string | null;
	/** The sport's coverage — games-wide, the same on every venue the sport plays at. */
	operators: MeetSportCoverage;
}

/**
 * One row of `GET /meets/status-counts/by-venue`: a top-level venue with the
 * sports played there. `venue` is null for the meets that have no venue yet
 * (listed last). Venues by name; sports in programme order.
 */
export interface MeetVenueBoard {
	venue: MeetBoardVenue | null;
	/** Top-level meets counted at this venue. */
	total: number;
	sports: MeetVenueSport[];
}

// ─── Activity ────────────────────────────────────────────────────────────────

/**
 * The activity keyset cursor: `<occurred_at as UTC text, microseconds>_<meet_id>`
 * — the same shape as the schedule's. Opaque: send `next_cursor` back as received.
 */
const strictUtcInstant = z.string().datetime();
const activityCursorField = z
	.string()
	.max(100)
	.regex(MEET_CURSOR_PATTERN, "Malformed cursor")
	// The regex accepts "2026-13-45T99:00:00Z": a real instant is checked too, or Postgres' cast would answer 500.
	.refine((v) => notYearZero(v) && strictUtcInstant.safeParse(v.slice(0, v.lastIndexOf("_"))).success, "Malformed cursor");

/** `GET /meets/:id/activity`. */
export const meetActivityQuery = keysetQuery.extend({ cursor: activityCursorField.optional() });
export type MeetActivityQuery = z.input<typeof meetActivityQuery>;

/** `GET /meets/activity` — one games. */
export const gamesMeetActivityQuery = keysetQuery.extend({ cursor: activityCursorField.optional(), games_id: idField });
export type GamesMeetActivityQuery = z.input<typeof gamesMeetActivityQuery>;

/**
 * What one write did to a meet (the rows of one transaction filed under it —
 * the meet, its slots, its officials), first match wins:
 *
 *   deleted     the meet was deleted
 *   created     the meet was created
 *   official    result_status became OFFICIAL
 *   reopened    result_status left OFFICIAL
 *   status      result_status or schedule_status changed otherwise
 *   results     a slot's result changed (value, rank, outcome, IRM, points)
 *   start_list  slots were added, removed, or their entries changed
 *   officials   the meet's officials changed
 *   edited      anything else on the meet (time, venue, remarks…)
 */
export const MEET_ACTIVITY_KINDS = [
	"deleted",
	"created",
	"official",
	"reopened",
	"status",
	"results",
	"start_list",
	"officials",
	"edited",
] as const;
export type MeetActivityKind = (typeof MEET_ACTIVITY_KINDS)[number];

/** The meet a write was filed under. Null fields when the meet no longer exists. */
export interface MeetActivityMeet {
	meet_id: string;
	meet_no: string | null;
	event_name: string | null;
	discipline_name: string | null;
	phase_name: string | null;
	sport: MeetSportSummary | null;
}

export interface MeetActivityActor {
	id: string;
	username: string;
	name: string | null;
}

/** One item of a meet activity feed: ONE write to ONE meet. */
export interface MeetActivityItem {
	kind: MeetActivityKind;
	/** The write's instant (every row of a transaction shares it). UTC. */
	occurred_at: string;
	meet: MeetActivityMeet;
	actor: MeetActivityActor | null;
	/** USER | AI_AGENT | SYSTEM | MIGRATION. */
	actor_type: string;
	/** The human headline the writer gave, when there is one. */
	reason: string | null;
	/** From the meet's own row, when it changed. */
	from_result_status: ResultStatus | null;
	to_result_status: ResultStatus | null;
	from_schedule_status: ScheduleStatus | null;
	to_schedule_status: ScheduleStatus | null;
	/** Slot rows the write touched (results, start list). */
	slots_changed: number;
}
